import { useEffect, useMemo, useState } from "react";
import { ArrowRight, CheckCircle2, Loader2, Search, Sparkles, Wand2, Wrench } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { apiFetch, ApiError } from "@/lib/api-client";
import { usePersona, type Persona } from "@/providers/persona-context";

function buildFirstRunSearch(sessionId: string | null) {
  const search = new URLSearchParams({ source: "onboarding" });

  if (sessionId) {
    search.set("legacy", "website-import");
  }

  return search;
}

interface OnboardingSessionRecord {
  id: string;
  status: string;
  detectedIndustry: string | null;
  crawlResults?: {
    logoUrl?: string | null;
  } | null;
  suggestedConfig?: {
    businessName?: string;
  } | null;
  acceptedConfig?: {
    businessName?: string;
    industry?: string;
    colors?: {
      primary?: string;
      secondary?: string;
    };
  } | null;
}

interface BootstrapResponse {
  site: {
    id: string;
  };
  redirectPageId: string | null;
}

interface AnalyzeResponse {
  sessionId: string;
  status: "suggestions_ready";
  extractedData: {
    businessName: string;
    industry: string;
    services: string[];
    hours: string;
    colors?: {
      primary?: string;
      secondary?: string;
    };
    logoUrl?: string | null;
  };
}

interface OnboardingAcceptedConfig {
  businessName: string;
  industry: string;
  services: string[];
  hours?: string;
  colors?: {
    primary?: string;
    secondary?: string;
  };
}

type OnboardingPersona = "owner" | "agency" | "team_member" | "client";

const ONBOARDING_PATHS = [
  {
    persona: "owner",
    label: "Business owner",
    eta: "5 min",
    runtimePersona: "operator",
    targetHref: null,
    description: "Set up the core CRM, booking, page, and first activation flow for your own business.",
    cta: "Use owner path",
  },
  {
    persona: "agency",
    label: "Agency",
    eta: "15 min",
    runtimePersona: "agency",
    targetHref: "/agency/first-client?source=onboarding",
    description: "Start with client intake, attribution, managed accounts, and proof-of-value setup.",
    cta: "Start agency path",
  },
  {
    persona: "team_member",
    label: "Team member",
    eta: "1 min",
    runtimePersona: "operator",
    targetHref: "/settings/team?source=onboarding",
    description: "Join the existing workspace, confirm your role, and land directly in team collaboration.",
    cta: "Join team path",
  },
  {
    persona: "client",
    label: "Client",
    eta: "30 sec",
    runtimePersona: "buyer",
    targetHref: "/portal?source=onboarding",
    description: "Enter the client portal quickly for messages, course access, history, and shared updates.",
    cta: "Open client path",
  },
] as const satisfies ReadonlyArray<{
  persona: OnboardingPersona;
  label: string;
  eta: string;
  runtimePersona: Persona;
  targetHref: string | null;
  description: string;
  cta: string;
}>;

const INDUSTRY_OPTIONS = [
  { value: "accounting", label: "Accounting" },
  { value: "dental", label: "Dental" },
  { value: "fitness", label: "Fitness" },
  { value: "hvac", label: "HVAC" },
  { value: "landscaping", label: "Landscaping" },
  { value: "legal", label: "Legal" },
  { value: "pet_grooming", label: "Pet Grooming" },
  { value: "plumbing", label: "Plumbing" },
  { value: "real_estate", label: "Real Estate" },
  { value: "restaurant", label: "Restaurant / Hospitality" },
  { value: "salon", label: "Salon" },
  { value: "spa", label: "Spa / Wellness" },
  { value: "tutoring", label: "Tutoring" },
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { setPersona } = usePersona();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session");
  const firstRunSearch = buildFirstRunSearch(sessionId);
  const firstRunQuery = firstRunSearch.toString();
  const aiGenerateHref = `/pages/ai-generate?${firstRunQuery}`;
  const builderEntryHref = `/pages/new?${firstRunQuery}`;
  const [resumeState, setResumeState] = useState<"idle" | "loading" | "error">("idle");
  const [resumeMessage, setResumeMessage] = useState<string | null>(null);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [analysisSessionId, setAnalysisSessionId] = useState<string | null>(null);
  const [acceptedConfig, setAcceptedConfig] = useState<OnboardingAcceptedConfig | null>(null);
  const [analysisLogoUrl, setAnalysisLogoUrl] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [selectedPersonaPath, setSelectedPersonaPath] = useState<OnboardingPersona>("owner");
  const [selectingPersonaPath, setSelectingPersonaPath] = useState<OnboardingPersona | null>(null);
  const [personaPathError, setPersonaPathError] = useState<string | null>(null);

  const hasLegacySession = Boolean(sessionId);
  const legacyResumeCopy = useMemo(() => {
    if (!sessionId) {
      return null;
    }

    if (resumeState === "loading") {
      return "We found a completed website-import session and are creating your first real site and starter pages now.";
    }

    if (resumeState === "error") {
      return resumeMessage ?? "We found your earlier website-import session, but the automatic builder handoff needs a manual retry.";
    }

    return "Your earlier website-import session was restored. We moved it into the same first-run path so you can keep going without switching onboarding systems.";
  }, [resumeMessage, resumeState, sessionId]);

  const canConfirmAnalyzedSetup = Boolean(
    analysisSessionId
    && acceptedConfig?.businessName.trim()
    && acceptedConfig?.industry.trim(),
  );

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    let isCancelled = false;

    const resumeIntoBuilder = async () => {
      setResumeState("loading");
      setResumeMessage(null);

      try {
        const session = await apiFetch<OnboardingSessionRecord>(`/api/onboarding/${sessionId}`);
        if (session.status !== "completed") {
          if (!isCancelled) {
            setResumeState("idle");
          }
          return;
        }

        const businessName = session.acceptedConfig?.businessName
          ?? session.suggestedConfig?.businessName
          ?? "";
        const vertical = session.detectedIndustry
          ?? session.acceptedConfig?.industry
          ?? "";

        if (!businessName || !vertical) {
          throw new Error("The completed onboarding session is missing the business name or vertical.");
        }

        const bootstrap = await apiFetch<{ data: BootstrapResponse }>("/crm/pages/sites/bootstrap", {
          method: "POST",
          body: JSON.stringify({
            businessName,
            vertical: vertical.replace(/_/g, "-"),
            logoUrl: session.crawlResults?.logoUrl ?? null,
            brandColors: {
              brand: session.acceptedConfig?.colors?.primary,
              secondary: session.acceptedConfig?.colors?.secondary,
            },
          }),
        });

        if (!isCancelled) {
          const targetPageId = bootstrap.data.redirectPageId;
          if (targetPageId) {
            navigate(`/pages/editor/${targetPageId}?siteId=${bootstrap.data.site.id}&entry=onboarding`, { replace: true });
            return;
          }

          navigate(`/pages/new?source=onboarding&siteId=${bootstrap.data.site.id}`, { replace: true });
        }
      } catch (error) {
        if (!isCancelled) {
          setResumeState("error");
          setResumeMessage(
            error instanceof ApiError
              ? error.message
              : error instanceof Error
                ? error.message
                : "We could not resume the onboarding handoff automatically.",
          );
        }
      }
    };

    void resumeIntoBuilder();

    return () => {
      isCancelled = true;
    };
  }, [navigate, sessionId]);

  const handleAnalyzeWebsite = async () => {
    if (!websiteUrl.trim()) {
      return;
    }

    setIsAnalyzing(true);
    setAnalyzeError(null);
    setConfirmError(null);

    try {
      const response = await apiFetch<AnalyzeResponse>("/api/onboarding/analyze", {
        method: "POST",
        body: JSON.stringify({ url: websiteUrl.trim() }),
      });

      setAnalysisSessionId(response.sessionId);
      setAnalysisLogoUrl(response.extractedData.logoUrl ?? null);
      setAcceptedConfig({
        businessName: response.extractedData.businessName || "",
        industry: response.extractedData.industry || "",
        services: response.extractedData.services ?? [],
        hours: response.extractedData.hours || undefined,
        colors: {
          primary: response.extractedData.colors?.primary,
          secondary: response.extractedData.colors?.secondary,
        },
      });
    } catch (error) {
      setAnalyzeError(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "We could not analyze that website.",
      );
      setAnalysisSessionId(null);
      setAcceptedConfig(null);
      setAnalysisLogoUrl(null);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSelectPersonaPath = async (personaPath: OnboardingPersona) => {
    const pathConfig = ONBOARDING_PATHS.find((path) => path.persona === personaPath);
    if (!pathConfig) {
      return;
    }

    setSelectingPersonaPath(personaPath);
    setPersonaPathError(null);

    try {
      await apiFetch<{ data: { persona: OnboardingPersona; targetMinutes: number } }>("/crm/onboarding/persona", {
        method: "POST",
        body: JSON.stringify({ persona: personaPath }),
      });
      setPersona(pathConfig.runtimePersona);
      setSelectedPersonaPath(personaPath);

      if (pathConfig.targetHref) {
        navigate(pathConfig.targetHref, { replace: false });
      }
    } catch (error) {
      setPersonaPathError(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "We could not save that onboarding path.",
      );
    } finally {
      setSelectingPersonaPath(null);
    }
  };

  const handleConfirmAnalyzedSetup = async () => {
    if (!analysisSessionId || !acceptedConfig) {
      return;
    }

    setIsConfirming(true);
    setConfirmError(null);

    try {
      await apiFetch<{ configured: boolean; created: string[] }>("/api/onboarding/confirm", {
        method: "POST",
        body: JSON.stringify({
          sessionId: analysisSessionId,
          acceptedConfig,
        }),
      });

      const bootstrap = await apiFetch<{ data: BootstrapResponse }>("/crm/pages/sites/bootstrap", {
        method: "POST",
        body: JSON.stringify({
          businessName: acceptedConfig.businessName,
          vertical: acceptedConfig.industry.replace(/_/g, "-"),
          logoUrl: analysisLogoUrl,
          brandColors: {
            brand: acceptedConfig.colors?.primary,
            secondary: acceptedConfig.colors?.secondary,
          },
        }),
      });

      const targetPageId = bootstrap.data.redirectPageId;
      if (targetPageId) {
        navigate(`/pages/editor/${targetPageId}?siteId=${bootstrap.data.site.id}&entry=onboarding`, { replace: true });
        return;
      }

      navigate(`/pages/new?source=onboarding&siteId=${bootstrap.data.site.id}`, { replace: true });
    } catch (error) {
      setConfirmError(
        error instanceof ApiError
          ? error.message
          : error instanceof Error
            ? error.message
            : "We could not apply the onboarding setup.",
      );
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--color-background)] px-6 py-10">
      <div className="mx-auto flex max-w-5xl flex-col gap-8">
        <section className="rounded-[32px] border border-[var(--color-border)] bg-[radial-gradient(circle_at_top_left,_color-mix(in_srgb,var(--color-primary)_18%,transparent),_transparent_42%),linear-gradient(135deg,color-mix(in_srgb,var(--color-foreground)_5%,transparent),transparent)] p-8 shadow-sm">
          <div className="max-w-3xl space-y-4">
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-background)]/80 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-[var(--color-muted-foreground)]">
              <Sparkles className="h-3.5 w-3.5" />
              First-Run Setup
            </span>
            <h1 className="text-3xl font-semibold tracking-tight text-[var(--color-foreground)]">
              Start with a guided first draft.
            </h1>
            <p className="max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
              The fastest path is simple: answer a few questions, review the AI draft section by section, then continue into the editor with a real page already started.
            </p>
            {hasLegacySession ? (
              <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)]/85 px-4 py-3 text-sm leading-6 text-[var(--color-muted-foreground)]">
                <div className="flex items-start gap-3">
                  {resumeState === "loading" ? <Loader2 className="mt-0.5 h-4 w-4 animate-spin text-[var(--color-primary)]" /> : null}
                  <span>{legacyResumeCopy}</span>
                </div>
              </div>
            ) : null}
            <div className="flex flex-col gap-3 sm:flex-row">
              <Link
                to={aiGenerateHref}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--color-primary)] px-5 py-3 text-sm font-semibold text-[var(--color-primary-foreground)] transition hover:opacity-95"
              >
                Start guided draft
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to={builderEntryHref}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-5 py-3 text-sm font-medium text-[var(--color-foreground)] transition hover:bg-[var(--color-accent)]"
              >
                See all builder paths
              </Link>
            </div>
          </div>
        </section>

        <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="space-y-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">
                <Sparkles className="h-3.5 w-3.5" />
                Persona Paths
              </span>
              <h2 className="text-xl font-semibold text-[var(--color-foreground)]">
                Pick the setup path that matches today&apos;s user.
              </h2>
              <p className="max-w-2xl text-sm leading-6 text-[var(--color-muted-foreground)]">
                Owner, agency, team, and client paths now persist the onboarding persona before routing into the matching CRM surface.
              </p>
            </div>
            <p className="rounded-full border border-[var(--color-border)] px-3 py-1 text-xs font-medium text-[var(--color-muted-foreground)]">
              Selected: {ONBOARDING_PATHS.find((path) => path.persona === selectedPersonaPath)?.label}
            </p>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            {ONBOARDING_PATHS.map((path) => {
              const isSelected = selectedPersonaPath === path.persona;
              const isLoading = selectingPersonaPath === path.persona;

              return (
                <button
                  key={path.persona}
                  type="button"
                  aria-pressed={isSelected}
                  disabled={selectingPersonaPath !== null}
                  onClick={() => { void handleSelectPersonaPath(path.persona); }}
                  className={[
                    "flex min-h-44 flex-col justify-between rounded-[22px] border p-5 text-left transition disabled:cursor-not-allowed disabled:opacity-70",
                    isSelected
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 shadow-sm"
                      : "border-[var(--color-border)] bg-[var(--color-background)] hover:border-[var(--color-primary)]/50 hover:bg-[var(--color-accent)]",
                  ].join(" ")}
                >
                  <span className="space-y-3">
                    <span className="flex items-start justify-between gap-3">
                      <span className="text-sm font-semibold text-[var(--color-foreground)]">{path.label}</span>
                      <span className="rounded-full bg-[var(--color-card)] px-2.5 py-1 text-xs font-semibold text-[var(--color-muted-foreground)]">
                        {path.eta}
                      </span>
                    </span>
                    <span className="block text-sm leading-6 text-[var(--color-muted-foreground)]">{path.description}</span>
                  </span>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[var(--color-primary)]">
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                    {isLoading ? "Saving path..." : path.cta}
                  </span>
                </button>
              );
            })}
          </div>

          {personaPathError ? (
            <div
              role="alert"
              aria-live="polite"
              data-testid="onboarding-persona-error"
              className="mt-4 rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]"
            >
              {personaPathError}
            </div>
          ) : null}
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          <article className="rounded-[24px] border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
            <div className="flex items-center gap-3 text-[var(--color-foreground)]">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                <Wand2 className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold">1. Tell us the basics</p>
                <p className="text-sm text-[var(--color-muted-foreground)]">Business, offer, customer, and goal.</p>
              </div>
            </div>
          </article>
          <article className="rounded-[24px] border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
            <div className="flex items-center gap-3 text-[var(--color-foreground)]">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                <CheckCircle2 className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold">2. Review the draft</p>
                <p className="text-sm text-[var(--color-muted-foreground)]">Approve each section before anything is saved.</p>
              </div>
            </div>
          </article>
          <article className="rounded-[24px] border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
            <div className="flex items-center gap-3 text-[var(--color-foreground)]">
              <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--color-primary)]/10 text-[var(--color-primary)]">
                <Wrench className="h-5 w-5" />
              </span>
              <div>
                <p className="text-sm font-semibold">3. Continue in the editor</p>
                <p className="text-sm text-[var(--color-muted-foreground)]">Polish the page without losing the guided context.</p>
              </div>
            </div>
          </article>
        </section>

        <section className="rounded-[28px] border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-sm">
          <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="space-y-4">
              <div className="space-y-2">
                <span className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--color-muted-foreground)]">
                  <Search className="h-3.5 w-3.5" />
                  Optional Website Analysis
                </span>
                <h2 className="text-xl font-semibold text-[var(--color-foreground)]">
                  Import your website into the same onboarding path.
                </h2>
                <p className="text-sm leading-6 text-[var(--color-muted-foreground)]">
                  If you already have a live site, we can analyze it, pre-fill the business profile, and let you approve the suggested setup before the builder draft starts.
                </p>
              </div>

              <div className="space-y-3">
                <label className="grid gap-2 text-sm">
                  <span className="font-medium text-[var(--color-foreground)]">Website URL</span>
                  <input
                    type="url"
                    value={websiteUrl}
                    onChange={(event) => setWebsiteUrl(event.target.value)}
                    placeholder="https://yourbusiness.com"
                    className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                  />
                </label>
                {analyzeError ? (
                  <div
                    role="alert"
                    aria-live="polite"
                    data-testid="onboarding-analyze-error"
                    className="rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]"
                  >
                    {analyzeError}
                  </div>
                ) : null}
                <button
                  type="button"
                  disabled={isAnalyzing || !websiteUrl.trim()}
                  onClick={handleAnalyzeWebsite}
                  className="inline-flex items-center gap-2 rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] px-4 py-3 text-sm font-medium text-[var(--color-foreground)] transition hover:bg-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isAnalyzing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  {isAnalyzing ? "Analyzing website..." : "Analyze website"}
                </button>
              </div>
            </div>

            <div className="rounded-[24px] border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              {acceptedConfig ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    {analysisLogoUrl ? (
                      <img
                        src={analysisLogoUrl}
                        alt=""
                        className="h-14 w-14 rounded-2xl border border-[var(--color-border)] object-cover"
                      />
                    ) : (
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-muted-foreground)]">
                        <Sparkles className="h-5 w-5" />
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-semibold text-[var(--color-foreground)]">Review the suggested setup</p>
                      <p className="text-sm leading-6 text-[var(--color-muted-foreground)]">
                        This uses the canonical onboarding session contract and waits for your approval before applying anything.
                      </p>
                    </div>
                  </div>

                  <label className="grid gap-2 text-sm">
                    <span className="font-medium text-[var(--color-foreground)]">Business name</span>
                    <input
                      value={acceptedConfig.businessName}
                      onChange={(event) => setAcceptedConfig((current) => current ? { ...current, businessName: event.target.value } : current)}
                      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                    />
                  </label>

                  <label className="grid gap-2 text-sm">
                    <span className="font-medium text-[var(--color-foreground)]">Industry</span>
                    <select
                      value={acceptedConfig.industry}
                      onChange={(event) => setAcceptedConfig((current) => current ? { ...current, industry: event.target.value } : current)}
                      className="rounded-xl border border-[var(--color-border)] bg-[var(--color-card)] px-4 py-3 text-sm text-[var(--color-foreground)]"
                    >
                      <option value="">Select industry</option>
                      {INDUSTRY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>

                  <div className="space-y-2 text-sm">
                    <p className="font-medium text-[var(--color-foreground)]">Detected services</p>
                    {acceptedConfig.services.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {acceptedConfig.services.map((service) => (
                          <span key={service} className="rounded-full border border-[var(--color-border)] bg-[var(--color-card)] px-3 py-1 text-xs text-[var(--color-muted-foreground)]">
                            {service}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[var(--color-muted-foreground)]">No services detected yet.</p>
                    )}
                  </div>

                  {confirmError ? (
                    <div
                      role="alert"
                      aria-live="polite"
                      data-testid="onboarding-confirm-error"
                      className="rounded-2xl border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 px-4 py-3 text-sm text-[var(--color-destructive)]"
                    >
                      {confirmError}
                    </div>
                  ) : null}

                  <button
                    type="button"
                    disabled={!canConfirmAnalyzedSetup || isConfirming}
                    onClick={handleConfirmAnalyzedSetup}
                    className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-primary)] px-4 py-3 text-sm font-semibold text-[var(--color-primary-foreground)] transition hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isConfirming ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                    {isConfirming ? "Applying setup..." : "Approve and continue to the builder"}
                  </button>
                </div>
              ) : (
                <div className="flex h-full min-h-[18rem] flex-col items-center justify-center gap-3 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] text-[var(--color-primary)]">
                    <Wand2 className="h-5 w-5" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-[var(--color-foreground)]">No second onboarding path</p>
                    <p className="text-sm leading-6 text-[var(--color-muted-foreground)]">
                      Analyze your site here if you want a pre-filled business profile. Otherwise, use the guided draft above and stay on the same builder-first onboarding path.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
